import { compare } from '@ember/utils'
import Controller from '@ember/controller'
import { service } from '@ember/service'
import { isEmpty, isPresent } from '@ember/utils'
import { optionTemplate as groupFilterDropdownOptionTemplate } from 'teacher-ui-client/utils/dropdown-templates'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import { buildDataOperationsConfigProxy } from 'teacher-ui-client/components/data-operations/data-operations-proxy'
import { SortType, SortDirection } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import { tracked } from '@glimmer/tracking'
import { TABLE_THEMES } from 'teacher-ui-client/manage/table-themes'
import { subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import maybeDecrementPageNumber from 'teacher-ui-client/components/data-operations/table/maybe-decrement-page-number'
import type AnalyticsService from 'teacher-ui-client/services/analytics'
import type SessionService from 'teacher-ui-client/services/session'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type RouterService from '@ember/routing/router-service'
import type Store from '@ember-data/store'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type TeacherSubscription from 'teacher-ui-client/models/teacher-subscription'
import type Teacher from 'teacher-ui-client/models/teacher'
import { cached } from '@glimmer/tracking'
import type { ManageClassesModel } from 'teacher-ui-client/manage/classes/route'
import {
  serialize as serializeSelected,
  deserialize as deserializeSelected,
} from '@blakeelearning/data-tables/utils/selecting'
import { hasId } from 'teacher-ui-client/utils/type-guards'
import type { CreateSchoolClassEvent } from 'teacher-ui-client/analytics/events'

export default class ManageClassesController extends Controller<ManageClassesModel> {
  @service analytics!: AnalyticsService

  @service session!: SessionService

  @service resourceConflict!: ResourceConflictService

  @service router!: RouterService

  @service store!: Store

  @cached
  get dataOperationsConfig() {
    return buildDataOperationsConfigProxy(this)
  }

  get currentTeacher() {
    return this.session.currentTeacher
  }

  get students() {
    return this.currentTeacher.students
  }

  get allSchoolClasses() {
    return this.model.allSchoolClasses
  }

  get school() {
    return this.currentTeacher.school
  }

  get isSubco() {
    return this.model.isSubco
  }

  get subscriptionType() {
    return this.model.subscriptionType
  }

  get tableTheme() {
    return TABLE_THEMES[subscriptionTypeToThemeKey(this.subscriptionType)]
  }

  /**
   * all teachers with a subscription for the current subscription type.
   */
  get allTeachers() {
    return this.model.teachers.slice()
  }

  /**
   * Non-trial teachers are determined by absence of a trial teacher-subscription.
   */
  get nonTrialTeachers() {
    return this.allTeachers.filter((teacher: Teacher) => !teacher.hasTrialSubscriptionFor(this.subscriptionType))
  }

  queryParams = [
    { sortCol: 'col' },
    { sortDir: 'dir' },
    { sortCompare: 'compare' },
    'selected',
    { currentPage: 'pageNumber' },
    { textFilter: 'filter' },
    { teacherFilter: 'teacherId' },
    { recordsPerPage: 'perPage' },
  ] as const

  @tracked sortCol = 'name'

  @tracked sortDir = SortDirection.Asc

  @tracked sortCompare = SortType.ByValue

  @tracked currentPage = 1

  @tracked recordsPerPage = 10

  @tracked textFilter = ''

  @tracked teacherFilter = ''

  @tracked selected = ''

  // Store the school-class we are leaving/deleting to pass to the confirmation modal Component
  @tracked schoolClassActingUpon?: SchoolClass

  @tracked leaveClassModalOpen = false

  @tracked deleteClassModalOpen = false

  @tracked deleteClassesModalOpen = false

  get selectedIds() {
    return deserializeSelected(this.selected, String)
  }

  set selectedIds(newSelectedIds) {
    this.selected = serializeSelected(newSelectedIds)
  }

  get editingRecordId() {
    return this.router.currentRoute?.params?.school_class_id
  }

  get hideJoinSchoolClass() {
    return this.isSubco || this.isTrial
  }

  get showJoinSchoolClass() {
    return !this.hideJoinSchoolClass
  }

  get showFilterByTeacher() {
    return this.isSubco
  }

  /**
   * @returns {Boolean} true if teacher has an unexpired trial subscription for the current subscription type.
   */
  get isTrial() {
    return this.currentTeacher.teacherSubscriptions.some(
      (teacherSub: TeacherSubscription) =>
        teacherSub.subscriptionType === this.subscriptionType && !teacherSub.isExpired,
    )
  }

  get mySchoolClasses() {
    return this.currentTeacher.schoolClasses
  }

  groupFilterDropdownOptionTemplate = groupFilterDropdownOptionTemplate

  get schoolClasses() {
    const allSchoolClasses = this.model.allSchoolClasses
      .filter((schoolClass) => schoolClass.subscriptionType === this.subscriptionType)
      .filter(hasId)

    if (this.isSubco) {
      return allSchoolClasses
    } else {
      return allSchoolClasses.filter((schoolClass) => this.mySchoolClasses.includes(schoolClass))
    }
  }

  /**
   * Computes an array of teacher model instances for the drop-down in the create-school-class component.
   * As subco all teachers are passed to the component.
   * As teacher only the teacher is passed to the component.
   * @return {Array<TeacherModel>}
   */
  get teachersForCreateSchoolClass() {
    return this.isSubco ? this.allTeachers : [this.currentTeacher]
  }

  get teacherOptions() {
    return this.teachersForCreateSchoolClass.filter(hasId).map((teacher) => {
      const { id, fullName } = teacher
      const studentCount = teacher.studentsForSubscriptionType(this.subscriptionType)?.length ?? 0
      return { text: fullName, value: id, uid: id, studentCount }
    })
  }

  get teacherOptionsSorted() {
    return this.teacherOptions.sort((a, b) => compare(a.text, b.text))
  }

  get data() {
    const data = this.schoolClasses
    const { textFilter, teacherFilter } = this

    // if no filters are applied, return all data
    if (isEmpty(textFilter) && isEmpty(teacherFilter)) {
      return data
    }

    return data.filter((item) => {
      const teacherIds = item.teachers.map((teacher) => teacher.id)
      // keep the selected value, even if it doesn't match the filter
      if (isPresent(teacherFilter) && !teacherIds.includes(teacherFilter)) {
        return false
      }
      const teacherNames = item.teachers.map((t) => t.fullName).join(' ')

      const content = [item.name, item.students.length, teacherNames].join(' ')

      if (isEmpty(textFilter)) return item
      return content.toLowerCase().includes(textFilter.trim().toLowerCase())
    })
  }

  /**
   * Create a new school-class with a teacher attached. If we are not sending a teacherId we will simply
   * attach the currentTeacher to the new class
   */
  createSchoolClass = async (name: string, teacherId = 'nonExistentId') => {
    const teacher = this.store.peekRecord<Teacher>('teacher', teacherId) || this.currentTeacher
    const { subscriptionType, school } = this

    const schoolClass = {
      name,
      subscriptionType,
      school,
      teachers: [teacher],
      gamesAccess: true,
      playroomAccess: true,
      backgroundMusicDisabled: false,
      mathseedsPrimeAccess: subscriptionType === SubscriptionType.Maths,
    }

    const record = await this.store.createRecord<SchoolClass>('school-class', schoolClass).save()
    const createSchoolClassEventArgs: CreateSchoolClassEvent = [
      'Create class',
      {
        source: 'manual',
        subscriptionType,
      },
    ]
    this.analytics.capture(...createSchoolClassEventArgs)
    this.router.refresh('manage.classes') // get new query results with newly created class
    return record
  }

  setGroupFilter = (teacherFilter: string) => {
    this.teacherFilter = teacherFilter
    this.currentPage = 1
  }

  resetGroupFilter = () => {
    this.setGroupFilter('')
  }

  leaveClass = (schoolClassId: string) => {
    const schoolClass = this.schoolClasses.find((schoolClass) => schoolClass.id === schoolClassId)

    this.schoolClassActingUpon = schoolClass
    this.showLeaveClassModal(true)
  }

  deleteClass = (schoolClassId: string) => {
    const schoolClass = this.schoolClasses.find((schoolClass) => schoolClass.id === schoolClassId)

    this.schoolClassActingUpon = schoolClass
    this.showDeleteClassModal(true)
  }

  submitLeaveClassModal = async (
    {
      currentPage,
      currentPageRecords,
      goTo,
    }: {
      currentPage: number | undefined
      currentPageRecords: unknown[]
      goTo: (newPageNumber: number) => void
    },
    schoolClass: SchoolClass,
  ) => {
    schoolClass.teachers = schoolClass.teachers.filter((teacher) => teacher.id !== this.currentTeacher.id)
    this.schoolClassActingUpon = undefined

    try {
      await schoolClass.save()
      if (schoolClass.teachers.length === 0) {
        // that was the last teacher of the school-class,
        // assume the server has soft-deleted the school-class
        schoolClass.unloadRecord()
      }
      maybeDecrementPageNumber({ currentPage, currentPageRecords, goTo })
    } catch (error) {
      if (isConflictError(error)) {
        return this.resourceConflict.handleError('leave-class', error)
      } else {
        throw error
      }
    }
  }

  submitDeleteClassModal = async (
    {
      currentPage,
      currentPageRecords,
      goTo,
    }: {
      currentPage: number | undefined
      currentPageRecords: unknown[]
      goTo: (newPageNumber: number) => void
    },
    schoolClass: SchoolClass,
  ) => {
    this.schoolClassActingUpon = undefined

    maybeDecrementPageNumber({ currentPage, currentPageRecords, goTo })

    return schoolClass.destroyRecord()
  }

  setTextFilter = (textFilter: string) => {
    this.textFilter = textFilter
    this.currentPage = 1
  }

  showLeaveClassModal = (open: boolean) => {
    this.leaveClassModalOpen = open
  }

  showDeleteClassModal = (open: boolean) => {
    this.deleteClassModalOpen = open
  }

  showDeleteClassesModal = (open: boolean) => {
    this.deleteClassesModalOpen = open
  }

  deleteClassesSuccess = () => {
    this.selectedIds = []
    this.showDeleteClassesModal(false)
  }
}
